import * as React from "react";
import { Package, Plus, Trash2 } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { Modal } from "@/components/Modal.js";
import { InventoryPicker } from "@/features/inventory/InventoryPicker.js";
import { ScanToItemsModal } from "./ScanToItemsModal.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import { cn } from "@/lib/utils.js";
import type { GoalDetail } from "./types.js";

/**
 * "Items involved" panel + its Add-items modal.
 *
 * Shared by the placement views (find_home / reorganize) and the declutter view.
 * Owns its own item-linking state (search, selection, link/unlink) and calls
 * `onItemsChanged` after a mutation so the parent can refetch the goal.
 *
 * Extracted verbatim from the old GoalDetailPage monolith — no behavior change.
 */
export function GoalItemsPanel({
  goal,
  goalId,
  isOwner,
  onSetGoal,
  onItemsChanged,
  showLinkItems,
  setShowLinkItems,
}: {
  goal: GoalDetail;
  goalId: string;
  isOwner: boolean;
  /** Optimistic local update of the goal (used for unlink + undo). */
  onSetGoal: (goal: GoalDetail) => void;
  /** Refetch the goal from the server after a mutation. */
  onItemsChanged: () => Promise<void> | void;
  /** Controlled visibility of the "Add items" modal (lifted so sibling
   *  placement panels can open it too — matches the old monolith behavior). */
  showLinkItems: boolean;
  setShowLinkItems: (open: boolean) => void;
}) {
  const toast = useToast();
  const { aiEnabled } = useFeatureFlags();
  const [selectedItemIds, setSelectedItemIds] = React.useState<string[]>([]);
  const [linkingItems, setLinkingItems] = React.useState(false);
  // "Select existing" (InventoryPicker) vs "New scan" (ScanToItemsModal) — the
  // two ways to bring items into a Find-a-home/reorganize goal (F-f §2.1).
  const [addMode, setAddMode] = React.useState<"existing" | "scan">("existing");
  const [showScan, setShowScan] = React.useState(false);

  // Link a set of item IDs to the goal (shared by "select existing" and scan).
  const linkItemIds = React.useCallback(async (itemIds: string[]) => {
    if (itemIds.length === 0) return;
    await apiFetch(`/api/organizing-goals/${goalId}/items`, {
      method: "POST",
      body: JSON.stringify({ itemIds }),
    });
    await onItemsChanged();
  }, [goalId, onItemsChanged]);

  const handleLinkItems = async () => {
    if (selectedItemIds.length === 0) return;
    setLinkingItems(true);
    try {
      await linkItemIds(selectedItemIds);
      toast.show(`${selectedItemIds.length} item(s) linked`, "success");
      setShowLinkItems(false);
      setSelectedItemIds([]);
    } catch (err: any) {
      toast.show(err.message || "Failed to link items", "error");
    }
    setLinkingItems(false);
  };

  const handleUnlinkItem = async (itemId: string) => {
    const previousItems = goal.items;
    onSetGoal({ ...goal, items: goal.items.filter((gi) => gi.itemId !== itemId) });
    try {
      await apiFetch(`/api/organizing-goals/${goalId}/items/${itemId}`, { method: "DELETE" });
      toast.show(
        "Item unlinked",
        "success",
        {
          label: "Undo",
          onClick: async () => {
            try {
              await apiFetch(`/api/organizing-goals/${goalId}/items`, {
                method: "POST",
                body: JSON.stringify({ itemIds: [itemId] }),
              });
              await onItemsChanged();
            } catch {
              toast.show("Couldn't undo — the item wasn't re-linked.", "error");
            }
          },
        }
      );
    } catch (err: any) {
      onSetGoal({ ...goal, items: previousItems });
      toast.show(err.message || "Failed to unlink item", "error");
    }
  };

  const linkedItemIds = React.useMemo(() => goal.items.map((gi) => gi.itemId), [goal.items]);

  return (
    <>
      <div className="bg-white rounded-2xl border border-neutral-border/30 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Package className="w-4 h-4 text-gray-400" />Items involved
          </h2>
          {isOwner && (
            <button
              onClick={() => { setShowLinkItems(true); }}
              className="flex items-center gap-1 text-xs text-primary font-medium hover:text-primary/80 py-2"
            >
              <Plus className="w-3.5 h-3.5" />Add items
            </button>
          )}
        </div>

        {goal.items.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-gray-500 mb-1">
              No items linked yet.
            </p>
            <p className="text-xs text-gray-400 mb-3">
              Link items to see how close they already are to where you need them.
            </p>
            {isOwner && (
              <button
                onClick={() => setShowLinkItems(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary/10 text-primary rounded-xl text-sm font-medium hover:bg-primary/20 transition-colors"
              >
                <Plus className="w-4 h-4" />Add items to get started
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {goal.items.map((gi) => (
              <div key={gi.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50">
                <div>
                  <span className="text-sm font-medium text-gray-700">{gi.itemName}</span>
                  {gi.spaceName && <span className="text-xs text-gray-400 ml-2">📍 {gi.spaceName}</span>}
                  {gi.itemPlannedSpaceName && <span className="text-xs text-amethyst-ink ml-2">→ moving to {gi.itemPlannedSpaceName}</span>}
                </div>
                {isOwner && (
                  <button onClick={() => handleUnlinkItem(gi.itemId)} className="text-gray-300 hover:text-red-400 p-2.5 -mr-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add items modal — two ways in (F-f §2.1): pick existing (shared
          InventoryPicker, F-c) or scan a photo (create-or-match). */}
      <Modal
        open={showLinkItems}
        onClose={() => { setShowLinkItems(false); setSelectedItemIds([]); }}
        title="Add items to this goal"
      >
        <div className="space-y-4">
          {aiEnabled && (
            <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-gray-100">
              <button
                onClick={() => setAddMode("existing")}
                className={cn(
                  "py-1.5 rounded-lg text-xs font-medium transition-colors",
                  addMode === "existing" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                )}
              >
                Select existing
              </button>
              <button
                onClick={() => setAddMode("scan")}
                className={cn(
                  "py-1.5 rounded-lg text-xs font-medium transition-colors",
                  addMode === "scan" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                )}
              >
                New scan
              </button>
            </div>
          )}

          {addMode === "existing" || !aiEnabled ? (
            <>
              <InventoryPicker
                selectedIds={selectedItemIds}
                onChange={setSelectedItemIds}
                excludeItemIds={linkedItemIds}
              />
              <button
                onClick={handleLinkItems}
                disabled={selectedItemIds.length === 0 || linkingItems}
                className="w-full py-3 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {linkingItems ? "Linking..." : `Link ${selectedItemIds.length} item(s)`}
              </button>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-600 mb-1">Snap a photo and we'll detect the items.</p>
              <p className="text-xs text-gray-400 mb-4">New items are created and linked; ones you already have are matched, not duplicated.</p>
              <button
                onClick={() => { setShowLinkItems(false); setShowScan(true); }}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Start a scan
              </button>
            </div>
          )}
        </div>
      </Modal>

      {/* Shared scan → create-or-match → link into the goal (F-f). */}
      <ScanToItemsModal
        open={showScan}
        onClose={() => setShowScan(false)}
        targetSpaceId={goal.activityLocationId}
        onResolved={async (itemIds) => {
          try {
            await linkItemIds(itemIds);
            toast.show(`${itemIds.length} item(s) added to this goal`, "success");
          } catch (err: any) {
            toast.show(err.message || "Added to inventory, but couldn't link to the goal", "error");
          }
        }}
        title="Scan items for this goal"
      />
    </>
  );
}
